import { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Upload, Crosshair, Play, Trash2, Loader2, Plane } from "lucide-react";
import { api, errMsg, rawUrl } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { AlignDialog } from "@/components/AlignDialog";

const BRONNEN = ["Drone", "Eigen luchtfoto", "Foto inspecteur", "Satelliet", "Anders"];

const UploadForm = ({ addressId, onDone }) => {
  const file = useRef(null);
  const [f, setF] = useState({ datum: new Date().toISOString().slice(0, 10), bron: "Drone", notitie: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!file.current.files[0]) return toast.error("Kies een afbeelding");
    const fd = new FormData();
    fd.append("file", file.current.files[0]);
    Object.entries(f).forEach(([k, v]) => fd.append(k, v));
    setBusy(true);
    try {
      await api.post(`/addresses/${addressId}/uploads`, fd);
      toast.success("Beeld gekoppeld aan perceel");
      file.current.value = "";
      onDone();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-2" data-testid="upload-form">
      <input ref={file} type="file" accept="image/jpeg,image/png,image/webp,image/tiff,.tif,.tiff" data-testid="upload-file-input" className="block w-full text-xs text-slate-400 file:mr-3 file:rounded file:border-0 file:bg-slate-800 file:px-3 file:py-1.5 file:text-slate-200" />
      <div className="grid grid-cols-2 gap-2">
        <Input type="date" data-testid="upload-date-input" value={f.datum} onChange={(e) => setF({ ...f, datum: e.target.value })} className="h-9 bg-slate-950 border-slate-800 text-xs" />
        <select data-testid="upload-source-select" value={f.bron} onChange={(e) => setF({ ...f, bron: e.target.value })} className="h-9 rounded-md bg-slate-950 border border-slate-800 px-2 text-xs">
          {BRONNEN.map((b) => <option key={b}>{b}</option>)}
        </select>
      </div>
      <Input data-testid="upload-note-input" placeholder="Notitie (optioneel)" value={f.notitie} onChange={(e) => setF({ ...f, notitie: e.target.value })} className="h-9 bg-slate-950 border-slate-800 text-xs" />
      <button data-testid="upload-submit-btn" disabled={busy} className="w-full h-9 rounded-md border border-slate-700 hover:border-blue-500 text-xs flex items-center justify-center gap-2">
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Beeld uploaden
      </button>
    </form>
  );
};

export const UploadsPanel = ({ address, baselineYear }) => {
  const qc = useQueryClient();
  const [align, setAlign] = useState(null);
  const [busy, setBusy] = useState(null);
  const key = ["uploads", address.id];
  const { data = [] } = useQuery({ queryKey: key, queryFn: () => api.get(`/addresses/${address.id}/uploads`).then((r) => r.data) });
  const reload = () => qc.invalidateQueries({ queryKey: key });

  const analyze = async (u) => {
    setBusy(u.id);
    try {
      const { data: r } = await api.post(`/addresses/${address.id}/analyze`, null, { params: { upload_id: u.id, force: true } });
      toast.success(r.melding_id ? "Wijziging gedetecteerd — melding aangemaakt" : "Geen relevante wijziging gevonden");
      qc.invalidateQueries();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(null);
    }
  };
  const remove = async (u) => {
    if (!window.confirm("Beeld verwijderen?")) return;
    await api.delete(`/uploads/${u.id}`);
    reload();
  };

  return (
    <div className="border border-slate-800 bg-[#111827]/80 rounded-lg p-5" data-testid="uploads-panel">
      <h3 className="font-display font-semibold mb-1 flex items-center gap-2"><Plane className="h-4 w-4" /> Eigen beelden ({data.length})</h3>
      <p className="text-[11px] text-slate-500 mb-3">Koppel een dronebeeld of andere opname aan dit perceel. GeoTIFF wordt exact geplaatst, drone-JPG met GPS automatisch geschat.</p>
      <UploadForm addressId={address.id} onDone={reload} />
      <div className="mt-4 space-y-3">
        {data.map((u) => (
          <div key={u.id} className="flex gap-3 border border-slate-800 rounded-md p-2" data-testid={`upload-item-${u.id}`}>
            <img src={rawUrl(u.id)} alt={u.filename} className="h-16 w-16 rounded object-cover bg-slate-900 shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium">{u.bron} · <span className="font-mono text-xs">{u.datum}</span></div>
              <div className="text-[11px] text-slate-500 truncate">{u.notitie || u.filename}</div>
              {u.georef === "gps" && u.georef_info && !u.georef_info.volledig && <div className="text-[10px] text-amber-400">Alleen positie uit GPS; schaal/rotatie handmatig bijstellen</div>}
              <div className="flex gap-1.5 mt-1.5">
                {u.georef === "geotiff" ? (
                  <span className="text-[11px] px-2 py-1 rounded bg-lime-500/15 text-lime-300 flex items-center gap-1" data-testid={`upload-georef-${u.id}`}><Crosshair className="h-3 w-3" />GeoTIFF · exact</span>
                ) : (
                <button data-testid={`upload-align-btn-${u.id}`} onClick={() => setAlign(u)} className={`text-[11px] px-2 py-1 rounded flex items-center gap-1 ${u.uitgelijnd ? "bg-slate-800 text-slate-300" : "bg-amber-500/20 text-amber-300"}`}>
                  <Crosshair className="h-3 w-3" />{u.georef === "gps" ? "GPS · bijstellen" : u.uitgelijnd ? "Uitgelijnd" : "Uitlijnen"}
                </button>
                )}
                <button data-testid={`upload-analyze-btn-${u.id}`} disabled={!!busy} onClick={() => analyze(u)} className="text-[11px] px-2 py-1 rounded bg-blue-600 hover:bg-blue-500 flex items-center gap-1 disabled:opacity-50">
                  {busy === u.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3" />} Vergelijk
                </button>
                <button data-testid={`upload-delete-btn-${u.id}`} onClick={() => remove(u)} className="text-[11px] px-1.5 py-1 rounded text-slate-500 hover:text-rose-400"><Trash2 className="h-3 w-3" /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {align && <AlignDialog upload={align} address={address} baselineYear={baselineYear} onClose={() => setAlign(null)} onSaved={() => { setAlign(null); reload(); }} />}
    </div>
  );
};
